import { ApiCropData, Bundle, CropApiImageItem } from '../../common/requestsTypings';
import { ADAPTIVE_SIZES, CreativeType, ResourceType } from '../../common/typings';
import { CommonOptionGroupName, CreativeOptionsData, CreativeOptionType } from '../../CreativeOptions/typings';
import { CreativeImageCropValue } from '../../CreativeSmartCenterModal/typings';
import { getCommonPcodeRenderData } from '../AddCommonCreative/helpers';
import { getCroppedMediaSetItem, getMediaSet } from '../helpers';
import {
    AdaptiveCreativePreviewItem,
    AddAdaptiveCreativeState,
    AddCreativeState,
    CommonCreativeItem
} from '../typings';

export function getAdaptiveModalPreviewItems(
    item: CommonCreativeItem,
    options: CreativeOptionsData,
    bundle: Bundle
): AdaptiveCreativePreviewItem[] {
    return ADAPTIVE_SIZES.map((size, index) => ({
        id: index,
        data: size,
        selected: true,
        pcodeData: getCommonPcodeRenderData(
            {
                ...item,
                data: size
            },
            options,
            bundle,
            true
        )
    }));
}

export function getAdaptiveModalPreviewItemsAfterSmartCenter(
    state: AddAdaptiveCreativeState,
    croppedFile: CropApiImageItem,
    smartCenter: ApiCropData
): AdaptiveCreativePreviewItem[] | undefined {
    const { item, creativeOptions, bundle, editingFileInfo } = state;

    if (!item || !creativeOptions || !bundle || !editingFileInfo) {
        return undefined;
    }

    const previewItems = state.previewItems
        ? state.previewItems.slice()
        : getAdaptiveModalPreviewItems(item, creativeOptions, bundle);
    const mediaSet = editingFileInfo.mediaSetId;

    previewItems.forEach(previewItem => {
        if (!previewItem.pcodeData) {
            return;
        }

        previewItem.pcodeData.mediaSets[mediaSet].items[0].items[0] = getCroppedMediaSetItem(
            croppedFile,
            getMediaSet(previewItem.pcodeData.mediaSets, mediaSet).alias,
            editingFileInfo.fileId,
            smartCenter
        );
    });

    return previewItems;
}

export function getSmartCentersValue(item: CommonCreativeItem, mediaSetId: string): CreativeImageCropValue | undefined {
    const mediaSet = getMediaSet(item.mediaSets, mediaSetId);
    const smartCenters = mediaSet.smartCenters;

    if (!smartCenters) {
        return undefined;
    }

    const smartCenter = smartCenters[0];

    return {
        crop: {
            x: smartCenter.x,
            y: smartCenter.y,
            width: smartCenter.w,
            height: smartCenter.h
        }
    };
}

export function getSmartCentersValueFromPreview(
    item: AdaptiveCreativePreviewItem,
    mediaSetId: string
): CreativeImageCropValue | undefined {
    const mediaSet = getMediaSet(item.pcodeData.mediaSets, mediaSetId);
    const smartCenters = mediaSet.smartCenters;

    if (!smartCenters) {
        return undefined;
    }

    const smartCenter = smartCenters[0];

    return {
        crop: {
            x: smartCenter.x,
            y: smartCenter.y,
            width: smartCenter.w,
            height: smartCenter.h
        }
    };
}

export function smartCenterEnabled(state: AddAdaptiveCreativeState): boolean {
    const { item, creativeOptions } = state;

    if (!item || !creativeOptions) {
        return false;
    }
    const optionGroupValue = creativeOptions.optionsValue.find(v => v.type === CommonOptionGroupName.image);

    if (!optionGroupValue) {
        return false;
    }

    const mediaSet = optionGroupValue.mediaSet;

    if (!mediaSet) {
        return false;
    }

    return Boolean(getMediaSet(item.mediaSets, mediaSet).fileId);
}

export function isOptionSmartCenterable(groupName: string, options: CreativeOptionsData): boolean {
    const groupConfig = options.optionsConfig[groupName].options;
    const option = groupConfig.find(
        el => el.type === CreativeOptionType.File && el.resourceType === ResourceType.image
    );

    if (!option || option.type !== CreativeOptionType.File || option.resourceType !== ResourceType.image) {
        return false;
    }

    return Boolean(option.isSmartCenterable);
}

export const HIDDEN_PRESET_PARAMETERES: CommonOptionGroupName[] = [
    CommonOptionGroupName.disclaimer,
    CommonOptionGroupName.domain,
    CommonOptionGroupName.logo,
    CommonOptionGroupName.description,
    CommonOptionGroupName.ageRestriction,
    CommonOptionGroupName.legal
];

export const getAdaptiveCreativeState = (state: AddCreativeState): AddAdaptiveCreativeState | undefined => {
    switch (state.type) {
        case CreativeType.ADAPTIVE:
            return state;

        default:
            return undefined;
    }
};
